import { format } from 'date-fns/format';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { cn } from '@/entrypoints/utils';
import {
  calendarConfigAtom,
  productSesamisAtom,
  selectedDayAtom,
  selectedMonthAtom,
  selectedProductAtom,
  selectedTimeSlotAtom,
  todayAtom,
} from './calendar-atoms';

type Props = {};

function CalendarHead({}: Props) {
  const { labels } = useAtomValue(calendarConfigAtom);
  const [selectedMonth, setSelectedMonth] = useAtom(selectedMonthAtom);
  const today = useAtomValue(todayAtom);

  const formattedMonth = useMemo(() => {
    return format(selectedMonth, 'MMMM yyyy');
  }, [selectedMonth, format]);

  const productSesamis = useAtomValue(productSesamisAtom);
  const setSelectedDay = useSetAtom(selectedDayAtom);
  const setSelectedTimeSlot = useSetAtom(selectedTimeSlotAtom);

  const changeMonth = useCallback(
    (direction: 'prev' | 'next') => {
      const newMonth = new Date(selectedMonth);
      direction === 'prev'
        ? newMonth.setMonth(newMonth.getMonth() - 1)
        : newMonth.setMonth(newMonth.getMonth() + 1);
      setSelectedMonth(newMonth);
    },
    [selectedMonth],
  );

  const handleNextClick = useCallback(() => {
    setSelectedDay(null);
    setSelectedTimeSlot(null);

    changeMonth('next');

    Array.from(productSesamis.values()).forEach((sesami) => {
      sesami.next('month');
    });
  }, [changeMonth, productSesamis]);

  const handlePrevClick = useCallback(() => {
    if (
      selectedMonth.getMonth() === today.getMonth() &&
      selectedMonth.getFullYear() === today.getFullYear()
    ) {
      return;
    }

    setSelectedDay(null);
    setSelectedTimeSlot(null);

    changeMonth('prev');

    Array.from(productSesamis.values()).forEach((sesami) => {
      sesami.previous('month');
    });
  }, [changeMonth, productSesamis]);

  return (
    <div className="medispa-calendar_head">
      <button
        type="button"
        onClick={handlePrevClick}
        className={cn(
          'medispa-calendar_prev',
          selectedMonth.getMonth() === today.getMonth() &&
            selectedMonth.getFullYear() === today.getFullYear() &&
            'disabled',
        )}
      >
        <svg
          width="16"
          height="12"
          viewBox="0 0 16 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ transform: 'scaleX(-1)' }}
        >
          <path
            d="M1 6L15 6M15 6L9.75 11.25M15 6L9.75 0.75"
            stroke="black"
            strokeLinecap="round"
            strokeLinejoin="round"
          ></path>
        </svg>
        <span className="desktop">{labels.prev.desktop}</span>
        <span className="mobile">{labels.prev.mobile}</span>
      </button>
      <h6 className="medispa-calendar_head-current">{formattedMonth}</h6>
      <button type="button" onClick={handleNextClick} className="medispa-calendar_next">
        <span className="desktop">{labels.next.desktop}</span>
        <span className="mobile">{labels.next.mobile}</span>
        <svg
          width="16"
          height="12"
          viewBox="0 0 16 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 6L15 6M15 6L9.75 11.25M15 6L9.75 0.75"
            stroke="black"
            strokeLinecap="round"
            strokeLinejoin="round"
          ></path>
        </svg>
      </button>
    </div>
  );
}

export default CalendarHead;
